import React, { useEffect, useState } from 'react';
import { Link, type MetaFunction } from 'react-router';
import { motion } from 'motion/react';

import transparentPattern from '~/assets/images/pattern-transparent.png';
import Card from '~/components/Card';
import DailyRiddleTimer from '~/components/DailyRiddleTimer';
import MultiplayerModeModal from '~/components/GameModesModals/MultiplayerModeModal';
import SingleplayerModeModal from '~/components/GameModesModals/SingleplayerModeModal';
import SurvivalModeModal from '~/components/GameModesModals/SurvivalModeModal';
import Icon from '~/components/Icon';
import Modal from '~/components/Modal';
import PwaInstallDrawer from '~/components/PwaInstallDrawer';
import SignIn from '~/components/SignIn';
import SocialLinks from '~/components/SocialLinks';

const backgroundImages = Array.from(
  { length: 50 },
  (_, i) => `/homepage/${i + 1}.webp`
);

export const meta: MetaFunction = ({ matches }) => {
  const parentMeta = matches
    .flatMap(match => match.meta ?? [])
    .filter(
      metaItem =>
        !('title' in metaItem) &&
        !('name' in metaItem && metaItem.name === 'description')
    );

  return [
    { title: 'Respawwwn - Game Guesser | Gaming Challenge' },
    {
      property: 'og:title',
      content: 'Respawwwn - Game Guesser | Gaming Challenge'
    },
    {
      name: 'description',
      content:
        'Test your gaming knowledge with daily challenges featuring 360° panoramas, screenshots, sounds and videos. Join thousands of gamers in the ultimate quiz experience.'
    },
    {
      property: 'og:description',
      content:
        'Test your gaming knowledge with daily challenges featuring 360° panoramas, screenshots, sounds and videos. Join thousands of gamers in the ultimate quiz experience.'
    },
    ...parentMeta
  ];
};

export default function Index() {
  // Modals state.
  const [isSurvivalModalOpen, setIsSurvivalModalOpen] = useState(false);
  const [isSingleplayerModalOpen, setIsSingleplayerModalOpen] = useState(false);
  const [isMultiplayerModalOpen, setIsMultiplayerModalOpen] = useState(false);

  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);

  useEffect(() => {
    const randomImage =
      backgroundImages[Math.floor(Math.random() * backgroundImages.length)];

    setBackgroundImage(randomImage);
  }, []);

  const modalSetters = {
    survival: setIsSurvivalModalOpen,
    singleplayer: setIsSingleplayerModalOpen,
    multiplayer: setIsMultiplayerModalOpen
  };

  const handleModalClick =
    (modalType: keyof typeof modalSetters) => (e: React.MouseEvent) => {
      e.preventDefault();
      modalSetters[modalType](true);
    };

  return (
    <div className="min-h-viewport pb-safe-offset-6 pt-safe-offset-20 md:pb-safe-offset-6 md:pt-safe-offset-6 relative flex flex-col overflow-hidden px-4 md:px-6">
      {/* Hidden H1 for SEO and accessibility */}
      <h1 className="sr-only">
        Respawwwn - Daily Gaming Challenge Platform - The Game Guessr platform
      </h1>

      {/* Background */}
      <div className="absolute inset-0 z-0 size-full">
        <div
          className="absolute inset-0 z-10 size-full"
          style={{
            backgroundImage: `url(${transparentPattern})`
          }}
        />
        <div className="left-gradient absolute inset-0 z-10 size-full" />
        {backgroundImage && (
          <motion.img
            initial={{ scale: 1.05, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.5 }}
            transition={{
              scale: {
                duration: 2.5,
                ease: 'circOut'
              },
              opacity: {
                duration: 0.5
              }
            }}
            src={backgroundImage}
            alt="background"
            className="size-full object-cover opacity-50"
            loading="eager"
          />
        )}
      </div>

      {/* Top bar — scoreboard + account. The logo is rendered by the root layout. */}
      <div className="relative mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 md:mb-6 md:flex-nowrap md:justify-end md:gap-10">
        <Link
          to="/leaderboard"
          data-cy="Scoreboard-link"
          className="h-base flex shrink-0 items-center gap-2.5 max-md:order-1"
        >
          <Icon name="scoreboard" size={5} />
          Scoreboard
        </Link>

        <SignIn stackOnMobile />
      </div>

      {/* Game modes */}
      <div className="relative grid gap-1.5 md:grid-cols-2 md:gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <Card
          to="/daily"
          label="Guess of the day"
          icon="question"
          variant="green"
          description={<DailyRiddleTimer inline />}
        />
        <Card
          label="Survival"
          icon="skull"
          variant="red"
          description="Compete against the clock."
          onClick={handleModalClick('survival')}
        />
        <Card
          to="/spotlight"
          label="Spotlight"
          icon="spotlight"
          variant="orange"
          description="Tailored collections."
        />
        <Card
          to="/crossword"
          label="Crossplay"
          icon="crossword"
          variant="blue"
          description="Crossword, leveled up."
        />
        <Card
          label="Singleplayer"
          icon="singleplayer"
          variant="purple"
          description="Customize your experience."
          onClick={handleModalClick('singleplayer')}
        />
        <Card
          label="Multiplayer"
          icon="multiplayer"
          variant="yellow"
          description="Challenge your friends."
          onClick={handleModalClick('multiplayer')}
        />
      </div>

      {/* Footer */}
      <div className="relative mt-auto flex flex-col gap-6 pt-12 md:flex-row md:items-end md:justify-between md:gap-10">
        <div className="flex flex-col gap-6">
          <SocialLinks />

          <div className="flex w-fit flex-col gap-1 lg:flex-row lg:gap-5">
            <Link to="/gameguessr/about" className="h-base">
              About us
            </Link>
            <Link to="/gameguessr/how-it-works" className="h-base">
              How it works
            </Link>
            <Link to="/gameguessr/contribute" className="h-base">
              Contribute
            </Link>
            <Link to="/blog" className="h-base">
              Blog
            </Link>
          </div>
        </div>

        <div className="flex w-fit flex-col gap-x-4 gap-y-0.5 *:whitespace-nowrap lg:flex-row">
          <Link to="/privacy-policy" className="p-sm">
            Privacy policy
          </Link>
          <Link to="/terms-and-conditions" className="p-sm">
            Terms and conditions
          </Link>
          <Link to="/copyright-complaints" className="p-sm">
            Copyright complaints
          </Link>
        </div>
      </div>

      {/* Survival Mode Modal */}
      <Modal
        isOpen={isSurvivalModalOpen}
        onClose={() => setIsSurvivalModalOpen(false)}
        size="small"
        hideCloseButton
      >
        <SurvivalModeModal onClose={() => setIsSurvivalModalOpen(false)} />
      </Modal>

      {/* Singleplayer Mode Modal */}
      <Modal
        isOpen={isSingleplayerModalOpen}
        onClose={() => setIsSingleplayerModalOpen(false)}
        size="small"
        hideCloseButton
      >
        <SingleplayerModeModal
          onClose={() => setIsSingleplayerModalOpen(false)}
        />
      </Modal>

      {/* Multiplayer Mode Modal */}
      <Modal
        isOpen={isMultiplayerModalOpen}
        onClose={() => setIsMultiplayerModalOpen(false)}
        size="small"
        hideCloseButton
      >
        <MultiplayerModeModal
          onClose={() => setIsMultiplayerModalOpen(false)}
        />
      </Modal>

      {/* PWA Install Drawer */}
      <PwaInstallDrawer />
    </div>
  );
}
